import '@vuepic/vue-datepicker/dist/main.css';
import 'mdui/mdui.css';
import './assets/main.css';

import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';
import { setupI18n } from './helpers/i18n';
import VueCookies from 'vue-cookies';

import { loadLocale } from 'mdui/functions/loadLocale.js';
import { setLocale } from 'mdui/functions/setLocale.js';

import AxiosFactory from '@/services/axiosFactory';

import App from './App.vue';
import router from './router';
import { useStore } from '@/store';
import { useSession } from '@/composables/session';

import InfoService from '@/services/infoService';

const infoService = new InfoService();

async function initSession() {
    //console.debug('[initSession] Starting...');
    try {
        const response = await infoService.GetMemberInfo();
        const member = response.data;

        // Guard: UmbracoMemberAuthorize returns a 302→HTML redirect when unauthenticated.
        // Axios follows the redirect and delivers a 200 with HTML — not a Member object.
        // Treat any response missing a valid email as "no session".
        if (!member?.email) {
            //console.debug('[initSession] GetMemberInfo returned no valid Member (got redirect/HTML). Treating as unauthenticated. Raw data:', member);
            return;
        }

        const session = useSession();
        session.setCurrentUser(member);
        //console.debug('[initSession] currentUser set:', member.email, '| isAdmin:', member.isAdmin);

        // Restore the previously selected debitor from sessionStorage (tab-scoped, cleared on tab close)
        const lastDebitorNumber = sessionStorage.getItem('lastDebitorNumber');
        const accounts = member.debitorAccounts;
        //console.debug('[initSession] sessionStorage lastDebitorNumber:', lastDebitorNumber);
        //console.debug('[initSession] debitorAccounts count:', accounts?.length, '| numbers:', accounts?.map(a => a.debitorNumber));

        if (lastDebitorNumber && accounts?.length) {
            const match = accounts.find((d) => d.debitorNumber === lastDebitorNumber);
            //console.debug('[initSession] Restoring debitor from sessionStorage — match found:', !!match, match?.debitorNumber);
            if (match) session.setCurrentDebitor(match);
        } else if (accounts?.length === 1) {
            // Auto-select when the user only has one debitor account
            //console.debug('[initSession] Auto-selecting single debitor account:', accounts[0].debitorNumber);
            session.setCurrentDebitor(accounts[0]);
        } else {
            //console.debug('[initSession] No debitor selected — user must choose from', accounts?.length, 'accounts');
        }
    } catch (e) {
        // 401 / network error — router will redirect to login as needed
        //console.debug('[initSession] Failed (likely 401 / no session):', e);
    }
}

async function validateSession() {
    try {
        await infoService.Validate();
    } catch (error: any) {
        if (error.response && error.response.status === 401) {
            console.warn("User permission revoked. Logging out...");
            localStorage.removeItem("store");
            useSession().clearSession();
            router.push({ path: "/login", query: { force: "true" } });
        }
    }
}

async function bootstrap() {
    const app = createApp(App);
    const pinia = createPinia();

    // STORE SETUP — load persisted UI preferences only
    if (localStorage.getItem('store')) {
        pinia.state.value = JSON.parse(localStorage.getItem('store')!);

        const rootStore = useStore(pinia);
        rootStore.refreshLanguages();

        if (pinia.state.value.store?.version !== import.meta.env.PACKAGE_VERSION) {
            pinia.state.value = {};
        }
    }

    // Persist UI preferences to localStorage
    watch(
        pinia.state,
        (state) => {
            if (!state?.store) return;
            localStorage.setItem('store', JSON.stringify({ store: state.store }));
        },
        { deep: true }
    );

    // Translations
    const lang = pinia.state.value?.store?.lang ?? document.documentElement.getAttribute('lang') ?? 'da-DK';
    AxiosFactory.setLanguage(lang);
    const i18n = setupI18n(lang);

    // Locale handling for MDUI internals
    loadLocale((locale) => import(`../node_modules/mdui/locales/${locale}.js`));

    const mduiSupportedLocales = ['en-us', 'zh-cn', 'zh-tw'];
    const normalizedLang = lang.toLowerCase();
    const mduiLocale = mduiSupportedLocales.includes(normalizedLang) ? normalizedLang : 'en-us';

    try {
        setLocale(mduiLocale);
    } catch (e) {
        setLocale('en-us');
    }

    // Fetch identity from server before mounting so the router guard sees currentUser immediately
    await initSession();

    // CONFIGURE APP
    app.use(VueCookies);
    app.use(pinia);
    app.use(router);
    app.use(i18n);

    // MOUNT APP
    app.mount('#app');

    // Background session check every 30 seconds
    if (useSession().currentUser.value) {
        setInterval(validateSession, 30000);
    }
}

bootstrap();
